/**
 * Rituals Page - Full dedicated page for daily rituals
 * Includes history, customisation, insights, and quick access to flows
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Sunrise, Moon, Play, ArrowLeft,
  Settings2, Calendar, Brain, Activity, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { RitualRoutineFlow } from '@/components/rituals/RitualRoutineFlow';
import { RitualStreakCard } from '@/components/rituals/RitualStreakCard';
import { RitualHistoryCalendar } from '@/components/rituals/RitualHistoryCalendar';
import { RitualCustomization } from '@/components/rituals/RitualCustomization';
import { RitualWhoopInsights } from '@/components/rituals/RitualWhoopInsights';
import { RitualAIInsights } from '@/components/rituals/RitualAIInsights';
import { RitualCelebration, isMilestoneStreak } from '@/components/rituals/RitualCelebration';
import { TomorrowActionsCard } from '@/components/rituals/TomorrowActionsCard';
import { SparkleGuide } from '@/components/guide/SparkleGuide';
import { RoutineType } from '@/components/rituals/types';
import { 
  useTodayRitualStatus, 
  useRitualStreak, 
  useYesterdayActions,
  useLogRitual,
  useReviewActions
} from '@/hooks/useRitualData';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const RITUAL_GUIDE_ITEMS = [
  { title: "Morning & Evening", description: "Complete both habits for best results" },
  { title: "Consistency", description: "Even a quick routine counts - showing up matters" },
  { title: "3 Actions", description: "Your evening '3 Things for Tomorrow' connects today to your future self" },
  { title: "Mood Tracking", description: "Track before and after to see the impact" },
  { title: "WHOOP Integration", description: "See how habits affect your recovery metrics" }
];

const Rituals: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showFlow, setShowFlow] = useState<RoutineType | null>(null);
  const [showTomorrowReview, setShowTomorrowReview] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationType, setCelebrationType] = useState<'completion' | 'streak_milestone' | 'both_complete'>('completion');

  const { data: todayStatus } = useTodayRitualStatus();
  const { data: streakData } = useRitualStreak();
  const { data: yesterdayActions } = useYesterdayActions();
  const logRitual = useLogRitual();
  const reviewActions = useReviewActions();

  const morningCompleted = todayStatus?.morning ?? false;
  const eveningCompleted = todayStatus?.evening ?? false;
  const streak = streakData?.currentStreak ?? 0;

  const hour = new Date().getHours();
  const isEvening = hour >= 17 || hour < 5;

  const handleRoutineComplete = async (data: {
    items_completed: string[];
    total_duration_minutes: number;
    mood_before: number;
    mood_after: number;
    tomorrow_actions?: any;
  }) => {
    try {
      await logRitual.mutateAsync({
        routineType: showFlow!,
        moodBefore: data.mood_before,
        moodAfter: data.mood_after,
        activitiesCompleted: data.items_completed,
        tomorrowActions: data.tomorrow_actions ? {
          action1: data.tomorrow_actions.actions?.[0] || '',
          action2: data.tomorrow_actions.actions?.[1] || '',
          action3: data.tomorrow_actions.actions?.[2] || '',
          futureConnection: data.tomorrow_actions.futureConnection,
        } : undefined,
      });

      const completedRoutine = showFlow;
      setShowFlow(null);

      // Determine celebration type
      const newStreak = streak + 1;
      const willBeBothComplete = 
        (completedRoutine === 'morning' && eveningCompleted) ||
        (completedRoutine === 'evening' && morningCompleted);

      if (willBeBothComplete) {
        setCelebrationType('both_complete');
      } else if (isMilestoneStreak(newStreak)) {
        setCelebrationType('streak_milestone');
      } else {
        setCelebrationType('completion');
      }
      
      setShowCelebration(true);

      toast({
        title: `${completedRoutine === 'morning' ? 'Morning' : 'Evening'} habit complete!`,
        description: data.mood_after > data.mood_before 
          ? `Your mood improved by ${data.mood_after - data.mood_before} points ✨`
          : "Well done for showing up for yourself.",
      });
    } catch (error) {
      console.error('Error saving habit:', error);
      toast({
        title: "Error saving habit",
        description: "Your progress was tracked but couldn't be saved.",
        variant: "destructive",
      });
    }
  };

  const handleTomorrowReviewSave = async (data: any) => {
    if (!yesterdayActions) return;

    try {
      await reviewActions.mutateAsync({
        actionId: yesterdayActions.id,
        action1Completed: data.completed?.[0] ?? false,
        action2Completed: data.completed?.[1] ?? false,
        action3Completed: data.completed?.[2] ?? false,
        barriers: data.barriers,
      });

      setShowTomorrowReview(false);
      toast({
        title: "Review saved",
        description: "Great reflection on yesterday's actions!",
      });
    } catch (error) {
      console.error('Error saving review:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Daily Healthy Habits | Therappi</title>
        <meta name="description" content="Build powerful morning and evening habits to support your wellbeing journey." />
      </Helmet>

      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/')}
                  className="shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-lg font-semibold flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Daily Healthy Habits
                  </h1>
                  <p className="text-xs text-muted-foreground">Morning & evening routines</p>
                </div>
              </div>
              <SparkleGuide items={RITUAL_GUIDE_ITEMS} title="Healthy Habits Tips" />
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Today's Status & Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Streak Card */}
            <RitualStreakCard />

            {/* Today's Rituals */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-muted-foreground">Today's Habits</h2>
                  {morningCompleted && eveningCompleted && (
                    <Badge className="bg-primary/20 text-primary border-0 gap-1">
                      <Sparkles className="w-3 h-3" />
                      All Complete!
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Morning */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => !morningCompleted && setShowFlow('morning')}
                    disabled={morningCompleted}
                    className={cn(
                      "p-4 rounded-xl border transition-all text-left",
                      morningCompleted 
                        ? "bg-secondary/10 border-secondary/30 cursor-default" 
                        : "bg-gradient-to-br from-secondary/5 to-background border-secondary/20 hover:border-secondary/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        morningCompleted ? "bg-secondary/20" : "bg-secondary/10"
                      )}>
                        <Sunrise className={cn(
                          "w-5 h-5",
                          morningCompleted ? "text-secondary" : "text-secondary/70"
                        )} />
                      </div>
                    </div>
                    <p className="font-medium">Morning</p>
                    <p className="text-xs text-muted-foreground">
                      {morningCompleted ? "Completed ✓" : "~20 min routine"}
                    </p>
                    {!morningCompleted && (
                      <div className="mt-3 flex items-center gap-1 text-xs text-secondary">
                        <Play className="w-3 h-3" />
                        Start
                      </div>
                    )}
                  </motion.button>

                  {/* Evening */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => !eveningCompleted && setShowFlow('evening')}
                    disabled={eveningCompleted}
                    className={cn(
                      "p-4 rounded-xl border transition-all text-left",
                      eveningCompleted 
                        ? "bg-primary/10 border-primary/30 cursor-default" 
                        : "bg-gradient-to-br from-primary/5 to-background border-primary/20 hover:border-primary/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        eveningCompleted ? "bg-primary/20" : "bg-primary/10"
                      )}>
                        <Moon className={cn(
                          "w-5 h-5",
                          eveningCompleted ? "text-primary" : "text-primary/70"
                        )} />
                      </div>
                    </div>
                    <p className="font-medium">Evening</p>
                    <p className="text-xs text-muted-foreground">
                      {eveningCompleted ? "Completed ✓" : "~25 min routine"}
                    </p>
                    {!eveningCompleted && (
                      <div className="mt-3 flex items-center gap-1 text-xs text-primary">
                        <Play className="w-3 h-3" />
                        Start
                      </div>
                    )}
                  </motion.button>
                </div>

                {/* Yesterday's Actions Review */}
                {yesterdayActions && !morningCompleted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTomorrowReview(true)}
                    className="w-full gap-2 border-accent/30 text-accent hover:bg-accent/10"
                  >
                    <Target className="w-4 h-4" />
                    Review yesterday's 3 actions
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Tabs for different sections */}
          <Tabs defaultValue="insights" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4 h-auto">
              <TabsTrigger value="insights" className="flex flex-col gap-1 py-2">
                <Brain className="w-4 h-4" />
                <span className="text-xs">Insights</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex flex-col gap-1 py-2">
                <Calendar className="w-4 h-4" />
                <span className="text-xs">History</span>
              </TabsTrigger>
              <TabsTrigger value="whoop" className="flex flex-col gap-1 py-2">
                <Activity className="w-4 h-4" />
                <span className="text-xs">WHOOP</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex flex-col gap-1 py-2">
                <Settings2 className="w-4 h-4" />
                <span className="text-xs">Customise</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="insights" className="space-y-4">
              <RitualAIInsights />
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <RitualHistoryCalendar />
            </TabsContent>

            <TabsContent value="whoop" className="space-y-4">
              <RitualWhoopInsights />
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <RitualCustomization />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Ritual Flow Dialog */}
      <Dialog open={showFlow !== null} onOpenChange={() => setShowFlow(null)}>
        <DialogContent className="max-w-lg p-0 border-0 bg-transparent">
          {showFlow && (
            <RitualRoutineFlow
              routineType={showFlow}
              onComplete={handleRoutineComplete}
              onClose={() => setShowFlow(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Tomorrow Actions Review Dialog */}
      <Dialog open={showTomorrowReview} onOpenChange={setShowTomorrowReview}>
        <DialogContent className="max-w-lg">
          {yesterdayActions && (
            <TomorrowActionsCard
              mode="review"
              actions={{
                action_1: yesterdayActions.action_1,
                action_2: yesterdayActions.action_2,
                action_3: yesterdayActions.action_3,
                action_1_completed: yesterdayActions.action_1_completed,
                action_2_completed: yesterdayActions.action_2_completed,
                action_3_completed: yesterdayActions.action_3_completed,
              }}
              onSave={handleTomorrowReviewSave}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Celebration Overlay */}
      <RitualCelebration
        show={showCelebration}
        type={celebrationType}
        streakCount={streak + 1}
        routineType={showFlow || undefined}
        onComplete={() => setShowCelebration(false)}
      />
    </>
  );
};

export default Rituals;
